import { Navigate } from "react-router-dom";
import {
  furthestSubFlowIndex,
  planSubFlows,
  previousStep,
  ONBOARDING_BASE_PATH,
  SUB_FLOW_STEP_ID,
} from "../../registry";
import { useFlowState } from "../../../state/FlowProvider";

/**
 * `/onboarding/about-you` itself, which is a redirect and never a page.
 *
 * The step has five routes beneath it and no screen of its own, so the bare
 * path has to choose one. It picks the furthest sub-flow *reached* rather than
 * the first, so a bookmark, a refresh, or arriving back from a later step
 * resumes where the member left off instead of walking them through answers
 * they have already given.
 *
 * It is also where `StepGuard` sends a mistyped or deselected goal, and where
 * the catch-all under this step sends an unknown path. Both mean the same
 * thing — "you belong in this step, just not on that sub-flow" — and both get
 * the same answer, decided once, here.
 */
const GoalSubflowIndex = ({ step }) => {
  const state = useFlowState();

  const plan = planSubFlows(state.goals);

  // Goals that map to no sub-flow at all — a hand-edited session, since the
  // guard has already established that `goals` is non-empty. There is nothing
  // to show, so send them back to choose again rather than to a blank screen.
  if (plan.length === 0) {
    const back = previousStep(SUB_FLOW_STEP_ID, state);
    return <Navigate to={`${ONBOARDING_BASE_PATH}/${back.path}`} replace />;
  }

  const furthest = furthestSubFlowIndex(state.goals, state.goalData);
  const target = plan[Math.min(furthest, plan.length - 1)];

  // `replace` so the bare path does not enter history — otherwise back from
  // the first sub-flow lands here and bounces straight forward again.
  return (
    <Navigate
      to={`${ONBOARDING_BASE_PATH}/${step.path}/${target.param}`}
      replace
    />
  );
};

export default GoalSubflowIndex;
